import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    CheckCircle,
    Users,
    Bell,
    Lock,
    LayoutDashboard,
    ChevronDown,
    ChevronUp,
    Mail,
    Heart,
    Shield,
    Clock,
    ArrowRight
} from 'lucide-react';

const LandingPage = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(null);
    const [isVisible, setIsVisible] = useState({});

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        document.querySelectorAll('section[id]').forEach((section) => {
            observer.observe(section);
        });

        return () => observer.disconnect();
    }, []);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            question: 'Is my data safe?',
            answer: 'Yes. We use bank-grade encryption to protect your family\'s information. Your data is never shared with third parties and is stored securely.'
        },
        {
            question: 'Will my parents/kids find it easy?',
            answer: 'Absolutely! mifamli is designed to be simple for all ages. The interface is intuitive, with large buttons and clear labels that anyone can use.'
        },
        {
            question: 'Do we all need separate logins?',
            answer: 'Yes, each family member will need a separate account to access the same shared Family Hub. This ensures secure access control, personalized permissions, and a clear audit trail of actions performed by every member.'
        },
        {
            question: 'How much does it cost?',
            answer: 'Free, during early access! We\'ll introduce a premium tier later with advanced features, but the core functionality will always be accessible for Free.'
        }
    ];

    return (
        <div className="relative min-h-screen">
            {/* Pulse animation for buttons */}
            <style>{`
                @keyframes pulse-glow {
                    0%, 100% {
                        box-shadow: 0 0 25px rgba(59, 130, 246, 0.6), 0 0 50px rgba(236, 72, 153, 0.6), 0 20px 60px rgba(0, 0, 0, 0.3);
                        transform: scale(1);
                    }
                    50% {
                        box-shadow: 0 0 35px rgba(59, 130, 246, 0.9), 0 0 70px rgba(236, 72, 153, 0.9), 0 20px 70px rgba(0, 0, 0, 0.4);
                        transform: scale(1.02);
                    }
                }
                .btn-pulse {
                    animation: pulse-glow 2s ease-in-out infinite;
                }
            `}</style>

            {/* Static Diagonal Gradient Background - Top-Left to Bottom-Right */}
            <div
                className="fixed inset-0 z-0"
                style={{
                    background: 'linear-gradient(135deg, rgb(59, 130, 246) 0%, rgb(236, 72, 153) 100%)'
                }}
            />

            {/* Navbar - Floating & Transparent with Frame Effect */}
            <nav className="fixed top-0 w-full z-50">
                <div className="relative w-full h-20 py-4">
                    {/* Logo - Positioned 10% from left */}
                    <div
                        className="fixed flex items-center space-x-3"
                        style={{ left: '10%', top: '1.5rem' }}
                    >
                        <img src="/logo.png" alt="mifamli logo" className="h-12 w-12" />
                        <span className="text-3xl font-bold text-white drop-shadow-lg">mifamli</span>
                    </div>

                    {/* Button - Positioned 10% from right */}
                    <div
                        className="fixed flex items-center gap-3"
                        style={{ right: '10%', top: '1.5rem' }}
                    >
                        <button
                            onClick={() => navigate('/login')}
                            className="btn-pulse relative px-8 py-4 rounded-xl font-bold text-xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(236, 72, 153, 0.8) 100%)',
                                boxShadow: '0 0 30px rgba(59, 130, 246, 0.7), 0 0 60px rgba(236, 72, 153, 0.7), 0 20px 70px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            <span className="relative z-10 flex items-center gap-3">
                                Join Early Access
                                <ArrowRight className="w-5 h-5" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/40 to-pink-500/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section
                id="hero"
                className={`relative z-10 pt-24 pb-16 sm:pt-32 sm:pb-20 px-4 transition-opacity duration-1000 ${isVisible.hero ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div className="relative z-20 text-center lg:text-left animate-slide-up w-full lg:pr-8">
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                                One App to Keep Your Family in Sync.
                            </h1>
                            <p className="text-3xl sm:text-4xl text-white/90 mb-8 leading-relaxed">
                                Tired of juggling bills, chores, passwords, and reminders? <br></br> mifamli puts everything your family needs <br></br>  — tasks, schedules, essentials <br></br>  — in one simple family app.
                            </p>
                            <button
                                onClick={() => navigate('/login')}
                                className="btn-pulse relative px-10 py-5 rounded-xl font-bold text-2xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group w-full sm:w-auto"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                    boxShadow: '0 0 35px rgba(59, 130, 246, 0.8), 0 0 70px rgba(236, 72, 153, 0.8), 0 20px 80px rgba(0, 0, 0, 0.4)'
                                }}
                            >
                                <span className="relative z-10 flex items-center justify-center gap-4">
                                    Join mifamli's Early Access
                                    <ArrowRight className="w-6 h-6" />
                                </span>
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                            </button>
                        </div>
                        <div className="relative z-10 animate-fade-in w-full flex justify-center lg:justify-end lg:pl-8">
                            <div className="relative w-full max-w-xl lg:max-w-2xl transform scale-[1.3] lg:scale-[1.5]" style={{ paddingBottom: '56.25%' }}>
                                <video
                                    src="/BackgroundVideo.mp4"
                                    className="absolute top-0 left-0 w-full h-full rounded-2xl shadow-2xl object-cover"
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Proposition Section */}
            <section
                id="benefits"
                className={`relative z-10 py-16 sm:py-100 px-4 transition-opacity duration-1000 ${isVisible.benefits ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-4">
                            The Calm We Promise.
                        </h2>
                        <p className="text-3xl sm:text-4xl text-white max-w-2xl mx-auto">
                            Family life shouldn't feel chaotic. <br></br> mifamli brings peace of mind by organizing everything that matters.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6 lg:gap-8 mb-12">
                        {[
                            {
                                icon: <CheckCircle className="w-8 h-8" />,
                                title: 'Shared to-do lists',
                                description: 'Everyone knows what\'s next'
                            },
                            {
                                icon: <Bell className="w-8 h-8" />,
                                title: 'Smart reminders',
                                description: 'Never miss bills, birthdays, or chores.'
                            },
                            {
                                icon: <Lock className="w-8 h-8" />,
                                title: 'Secure vault',
                                description: 'Safely store passwords and household info.'
                            },
                            {
                                icon: <LayoutDashboard className="w-8 h-8" />,
                                title: 'Unified dashboard',
                                description: 'Get a clear view of everything that matters'
                            }
                        ].map((benefit, index) => (
                            <div
                                key={index}
                                className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 flex items-start space-x-4"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className="text-primary-green flex-shrink-0 mt-1">
                                    {benefit.icon}
                                </div>
                                <div>
                                    <h3 className="text-3xl font-bold text-primary-blue mb-2">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-2xl text-gray-600">
                                        {benefit.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <button
                            onClick={() => navigate('/login')}
                            className="btn-pulse relative px-10 py-5 rounded-xl font-bold text-2xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group w-full sm:w-auto"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                boxShadow: '0 0 35px rgba(59, 130, 246, 0.8), 0 0 70px rgba(236, 72, 153, 0.8), 0 20px 80px rgba(0, 0, 0, 0.4)'
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-4">
                                Try mifamli for Free
                                <ArrowRight className="w-6 h-6" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                        </button>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section
                id="testimonials"
                className={`relative z-10 py-16 sm:py-100 px-4 transition-opacity duration-1000 ${isVisible.testimonials ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        Loved by Families Like Yours
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8 mb-12">
                        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center mb-4">
                                <img
                                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=100&h=100&fit=crop"
                                    alt="Riya"
                                    className="w-16 h-16 rounded-full object-cover mr-4"
                                />
                                <div>
                                    <p className="font-bold text-primary-blue text-2xl">Riya</p>
                                    <p className="text-xl text-gray-600">Bangalore</p>
                                </div>
                            </div>
                            <p className="text-2xl text-gray-700 italic leading-relaxed">
                                "Before mifamli, we lived in WhatsApp chaos. Now, everything is in one place; bills, groceries, tasks and we never miss a thing."
                            </p>
                        </div>

                        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-center mb-4">
                                <img
                                    src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop"
                                    alt="Megha"
                                    className="w-16 h-16 rounded-full object-cover mr-4"
                                />
                                <div>
                                    <p className="font-bold text-primary-blue text-2xl">Megha</p>
                                    <p className="text-xl text-gray-600">Working Mom, Hyderabad</p>
                                </div>
                            </div>
                            <p className="text-2xl text-gray-700 italic leading-relaxed">
                                "I used to carry the mental load alone, now we share tasks and it's such a relief!"
                            </p>
                        </div>
                    </div>

                    {/* Trust Indicators */}
                    <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 text-xl sm:text-2xl text-white">
                        <div className="flex items-center space-x-2">
                            <Users className="w-8 h-8 text-white" />
                            <span className="font-semibold">Trusted by 500+ families</span>
                        </div>
                        <div className="hidden sm:block text-white/50">·</div>
                        <div className="flex items-center space-x-2">
                            <Shield className="w-8 h-8 text-white" />
                            <span className="font-semibold">Privacy-first</span>
                        </div>
                        <div className="hidden sm:block text-white/50">·</div>
                        <div className="flex items-center space-x-2">
                            <Heart className="w-8 h-8 text-white" />
                            <span className="font-semibold">Built by parents</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section
                id="how-it-works"
                className={`relative z-10 py-16 sm:py-100 px-4 transition-opacity duration-1000 ${isVisible['how-it-works'] ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        How It Works (So Simple)
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8 mb-12">
                        {[
                            {
                                step: '1',
                                icon: <Users className="w-12 h-12" />,
                                title: 'Create Your Family Hub',
                                description: 'Invite your family in seconds.'
                            },
                            {
                                step: '2',
                                icon: <CheckCircle className="w-12 h-12" />,
                                title: 'Add Tasks, Trips & More',
                                description: 'All in one dashboard.'
                            },
                            {
                                step: '3',
                                icon: <Bell className="w-12 h-12" />,
                                title: 'Stay in Sync',
                                description: 'Smart reminders to stay on track.'
                            }
                        ].map((step, index) => (
                            <div
                                key={index}
                                className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center relative"
                            >
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary-orange text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                                    {step.step}
                                </div>
                                <div className="text-primary-green mb-4 flex justify-center mt-4">
                                    {step.icon}
                                </div>
                                <h3 className="text-3xl font-bold text-primary-blue mb-3">
                                    {step.title}
                                </h3>
                                <p className="text-2xl text-gray-600">
                                    {step.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="text-center">
                        <button
                            onClick={() => navigate('/login')}
                            className="btn-pulse relative px-10 py-5 rounded-xl font-bold text-2xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group w-full sm:w-auto"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                boxShadow: '0 0 35px rgba(59, 130, 246, 0.8), 0 0 70px rgba(236, 72, 153, 0.8), 0 20px 80px rgba(0, 0, 0, 0.4)'
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-4">
                                Create mifamli Hub Now
                                <ArrowRight className="w-6 h-6" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                        </button>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section
                id="faq"
                className={`relative z-10 py-16 sm:py-100 px-4 transition-opacity duration-1000 ${isVisible.faq ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white text-center mb-12">
                        Frequently Asked Questions
                    </h2>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-white/90 backdrop-blur-sm rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300"
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-100 transition-colors duration-200"
                                    style={{ minHeight: '44px' }}
                                >
                                    <span className="text-3xl font-semibold text-primary-blue pr-4">
                                        {faq.question}
                                    </span>
                                    {openFaq === index ? (
                                        <ChevronUp className="w-6 h-6 text-primary-orange flex-shrink-0" />
                                    ) : (
                                        <ChevronDown className="w-6 h-6 text-primary-orange flex-shrink-0" />
                                    )}
                                </button>
                                {openFaq === index && (
                                    <div className="px-6 pb-5 text-2xl text-gray-700 leading-relaxed animate-fade-in">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section
                id="final-cta"
                className={`relative z-10 py-16 sm:py-20 px-4 text-white transition-opacity duration-1000 ${isVisible['final-cta'] ? 'opacity-100' : 'opacity-0'}`}
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                        Life moves fast. <br></br> But your family can stay calm and connected.
                    </h2>
                    <p className="text-3xl sm:text-4xl mb-8 text-white/90">
                        Join hundreds of families simplifying daily life with mifamli.
                    </p>

                    <div className="max-w-md mx-auto mb-6">
                        <button
                            onClick={() => navigate('/login')}
                            className="btn-pulse relative px-10 py-5 rounded-xl font-bold text-2xl text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-4 border-white/50 overflow-hidden group w-full sm:w-auto"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                boxShadow: '0 0 35px rgba(59, 130, 246, 0.8), 0 0 70px rgba(236, 72, 153, 0.8), 0 20px 80px rgba(0, 0, 0, 0.4)'
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-4">
                                Take me to mifamli's Hub
                                <ArrowRight className="w-6 h-6" />
                            </span>
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                        </button>
                    </div>



                    <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 text-xl sm:text-2xl text-white/80">
                        <div className="flex items-center space-x-2">
                            <CheckCircle className="w-8 h-8" />
                            <span className="font-semibold">Free Early Access</span>
                        </div>
                        <span className="hidden sm:inline">·</span>
                        <div className="flex items-center space-x-2">
                            <Shield className="w-8 h-8" />
                            <span className="font-semibold">No Credit Card</span>
                        </div>
                        <span className="hidden sm:inline">·</span>
                        <div className="flex items-center space-x-2">
                            <Clock className="w-8 h-8" />
                            <span className="font-semibold">2-Minute Setup</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Made with Love */}
            <div className="relative z-10 px-4" style={{ paddingTop: '104px', paddingBottom: '104px' }}>
                <div className="max-w-6xl mx-auto text-center">
                    <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white flex items-center justify-center gap-4 flex-wrap">
                        <span>Made with</span>
                        <svg className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: 'rgb(239, 68, 68)', stopOpacity: 1 }} />
                                    <stop offset="100%" style={{ stopColor: 'rgb(220, 38, 38)', stopOpacity: 1 }} />
                                </linearGradient>
                            </defs>
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="url(#heartGradient)" stroke="url(#heartGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>in Bangalore</span>
                    </p>
                </div>
            </div>

            {/* Divider before Footer */}
            <div className="relative z-10 px-4">
                <div className="max-w-6xl mx-auto">
                    <hr className="border-t border-white/30" />
                </div>
            </div>

            {/* Footer */}
            <footer className="relative z-10 text-white py-12 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center space-x-3 mb-4">
                                <img src="/logo.png" alt="mifamli logo" className="h-8 w-8" />
                                <span className="text-3xl font-bold text-white">mifamli</span>
                            </div>
                            <p className="text-white text-xl">
                                The Family hub that keeps everyone in sync.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-3 text-2xl text-white">Product</h4>
                            <ul className="space-y-2 text-xl text-white">
                                <li><a href="#benefits" className="hover:opacity-80 transition-opacity">Features</a></li>
                                <li><a href="#how-it-works" className="hover:opacity-80 transition-opacity">How It Works</a></li>
                                <li><a href="#faq" className="hover:opacity-80 transition-opacity">FAQ</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-3 text-2xl text-white">Company</h4>
                            <ul className="space-y-2 text-xl text-white">
                                <li><Link to="/about" className="hover:opacity-80 transition-opacity">About Us</Link></li>
                                <li><Link to="/contact" className="hover:opacity-80 transition-opacity">Contact</Link></li>
                                <li><Link to="/blog" className="hover:opacity-80 transition-opacity">Blog</Link></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-3 text-2xl text-white">Legal</h4>
                            <ul className="space-y-2 text-xl text-white">
                                <li><Link to="/privacy" className="hover:opacity-80 transition-opacity">Privacy Policy</Link></li>
                                <li><Link to="/terms" className="hover:opacity-80 transition-opacity">Terms of Service</Link></li>
                                <li><Link to="/security" className="hover:opacity-80 transition-opacity">Security</Link></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-white/30 pt-8 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                        <p className="text-xl text-white">
                            © 2025 mifamli. All rights reserved.
                        </p>
                        <div className="flex space-x-6">
                            <a href="#" className="text-white hover:opacity-80 transition-opacity" aria-label="Twitter">
                                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                                </svg>
                            </a>
                            <a href="#" className="text-white hover:opacity-80 transition-opacity" aria-label="Instagram">
                                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                                </svg>
                            </a>
                            <a href="#" className="text-white hover:opacity-80 transition-opacity" aria-label="LinkedIn">
                                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Floating CTA Button (Mobile) */}
            <button
                onClick={() => navigate('/login')}
                className="btn-pulse fixed bottom-6 right-6 backdrop-blur-md border-4 border-white/50 text-white p-5 rounded-xl hover:scale-110 transform transition-all duration-200 lg:hidden z-40 relative overflow-hidden group"
                style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                    boxShadow: '0 0 30px rgba(59, 130, 246, 0.8), 0 0 60px rgba(236, 72, 153, 0.8), 0 15px 70px rgba(0, 0, 0, 0.4)'
                }}
                aria-label="Join early access"
            >
                <ArrowRight className="w-6 h-6 relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            </button>
        </div>
    );
};

export default LandingPage;
